import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, ChevronLeft, Shield, Truck, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { useUser } from '../context/UserContext';
import apiService from '../services/nextlevelgames-api';
import { StockBadges } from '../components/ProductCard';
import { STORES } from '../utils/stores';
import { parseCondition } from '../utils/cardCondition';
import { visibleCategory } from '../utils/reportingCategory';

const SINGLES_CATEGORIES = ['Magic Singles', 'Pokemon Singles'];

const CatalogDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { locations } = useStore();
  const { customer } = useUser();
  const [buyingNow, setBuyingNow] = useState(false);
  const [buyNowError, setBuyNowError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  // 'ship' or a store's location id for pickup
  const [fulfillment, setFulfillment] = useState('ship');

  const item = location.state?.item;

  // Compute quantity: if user had location(s) filtered, show sum for those; otherwise total across all locations
  const quantityAvailable = (() => {
    const qbl = item?.quantities_by_location;
    if (!qbl || Object.keys(qbl).length === 0) return 0;
    const activeLocations = item?.selectedLocationIds;
    if (activeLocations && activeLocations.length > 0) {
      return activeLocations.reduce((sum, id) => sum + (qbl[id] || 0), 0);
    }
    return Object.values(qbl).reduce((sum, q) => sum + (q || 0), 0);
  })();

  // Stores holding stock of this item, for the pickup options
  const pickupStores = STORES
    .map((store) => ({
      ...store,
      qty: Math.max(0, Math.floor(Number(item?.quantities_by_location?.[store.id]) || 0)),
      location: locations.find((l) => l.id === store.id),
    }))
    .filter((store) => store.qty > 0);

  const pickupStore = pickupStores.find((store) => store.id === fulfillment) || null;
  // Shipping can draw on every store's stock; pickup only on the chosen store's
  const maxQuantity = pickupStore ? pickupStore.qty : quantityAvailable;

  if (!item) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="container">
          <div className="text-center py-16">
            <h1 className="text-3xl font-bold mb-4">Item Not Found</h1>
            <p className="text-gray-500 mb-4">This item may have been accessed directly. Please browse from the shop.</p>
            <Link to="/shop" className="text-primary-600 hover:text-primary-700 font-medium">
              Return to Shop
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const onlineAvailable = item.online_available === true;

  const isSingle = item.categories?.some((cat) => SINGLES_CATEGORIES.includes(cat));
  const condition = isSingle ? parseCondition(item.variation_name || item.name) : null;

  const outOfStock = item.quantities_by_location !== undefined && quantityAvailable === 0;

  // Skips the cart: builds a checkout for just this item and sends the
  // customer straight to the payment page.
  const handleBuyNow = async () => {
    if (!onlineAvailable || buyingNow) return;
    setBuyingNow(true);
    setBuyNowError(null);
    try {
      const shipping = !pickupStore;
      const locationId = pickupStore ? pickupStore.id : item.location_id;
      const line = {
        id: item.variation_id || item.name,
        name: item.name,
        price: item.price,
        variation_id: item.variation_id,
        location_id: locationId,
        quantity,
      };
      const response = await apiService.createPaymentLink(
        [line],
        shipping,
        customer?.id || null,
        locationId,
        shipping ? 'SHIPMENT' : 'PICKUP'
      );
      const url = response?.payment_link?.url;
      if (!url) throw new Error('No payment link URL returned from API');
      window.location.assign(url);
    } catch (error) {
      setBuyNowError(error.message || 'Could not start checkout. Please try again.');
      setBuyingNow(false);
    }
  };

  const handleAddToCart = () => {
    if (!onlineAvailable) return;
    const cartProduct = {
      // See ProductCard: cart lines are keyed on variation_id so different
      // conditions of the same card stay separate.
      id: item.variation_id || item.name,
      name: item.name,
      variation_name: item.variation_name,
      price: item.price,
      category: item.category,
      image: item.imageUrl,
      set: item.description,
      variation_id: item.variation_id,
      location_id: item.location_id,
      present_at_location_ids: item.present_at_location_ids || [],
      quantities_by_location: item.quantities_by_location || {},
      // See ProductCard: events share the catalog with physical products.
      is_event: item.is_event === true,
    };
    const preference = pickupStore
      ? { method: 'pickup', locationId: pickupStore.id }
      : { method: 'shipping', locationId: null };
    for (let i = 0; i < quantity; i++) {
      addToCart(cartProduct, preference);
    }
  };

  const categoryPath = visibleCategory(item.category)?.toLowerCase() || 'shop';

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <button onClick={() => navigate(-1)} className="hover:text-primary-600 flex items-center">
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <span>/</span>
          <Link to="/shop" className="hover:text-primary-600">Shop</Link>
          {visibleCategory(item.category) && (
            <>
              <span>/</span>
              <Link to={`/shop/${categoryPath}`} className="hover:text-primary-600 capitalize">
                {categoryPath}
              </Link>
            </>
          )}
          <span>/</span>
          <span className="text-gray-900 truncate max-w-xs">{item.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                {condition && (
                  <span
                    className={`absolute top-3 right-3 text-sm px-3 py-1 rounded-full font-bold z-10 shadow-sm ${condition.colorClasses}`}
                    title={condition.label}
                  >
                    {condition.code}
                  </span>
                )}
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-[150px]">🎴</span>
                )}
              </div>
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-4">
              {/* Category Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {item.categories?.map((cat) => (
                  <span
                    key={cat}
                    className="bg-primary-50 text-primary-700 text-xs px-3 py-1 rounded-full font-semibold"
                  >
                    {cat}
                  </span>
                ))}
              </div>

              <h1 className="text-3xl font-bold mb-2">{item.name}</h1>

              {item.variation_name && (
                <p className="text-base text-gray-700 font-medium mb-3">{item.variation_name}</p>
              )}

              {item.description_html ? (
                <div
                  className="rich-description text-gray-600 mb-4"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description_html) }}
                />
              ) : item.description ? (
                <p className="text-lg text-gray-600 mb-4 whitespace-pre-line">{item.description}</p>
              ) : null}

              {/* Price */}
              <div className="border-t border-gray-100 pt-4 mb-4">
                <span className="text-3xl font-bold text-gray-900">
                  ${item.price.toFixed(2)}
                </span>
              </div>

              {/* Stock: total, plus how many at each store */}
              {item.quantities_by_location !== undefined && (
                <div className="mb-4">
                  {quantityAvailable > 0 ? (
                    <>
                      <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                        {quantityAvailable} in stock
                      </span>
                      <StockBadges quantities={item.quantities_by_location} />
                    </>
                  ) : (
                    <span className="inline-block bg-red-100 text-red-700 text-sm font-medium px-3 py-1 rounded-full">
                      Out of stock
                    </span>
                  )}
                </div>
              )}

              {/* How would you like this? */}
              {onlineAvailable && quantityAvailable > 0 && (
                <fieldset className="mb-5 border border-gray-200 rounded-lg p-4 bg-white">
                  <legend className="px-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Select Pickup/Shipping Option
                  </legend>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="fulfillment"
                        checked={fulfillment === 'ship'}
                        onChange={() => setFulfillment('ship')}
                        className="mt-1 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">Ship to me</span>
                        <span className="block text-xs text-gray-500">Shipping is calculated at checkout</span>
                      </span>
                    </label>
                    {pickupStores.map((store) => (
                      <label key={store.id} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="fulfillment"
                          checked={fulfillment === store.id}
                          onChange={() => {
                            setFulfillment(store.id);
                            setQuantity((q) => Math.min(q, store.qty));
                          }}
                          className="mt-1 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                        />
                        <span>
                          <span className="block text-sm font-medium text-gray-900">
                            Pick up at {store.name}
                            <span className="ml-2 text-xs font-normal text-green-700">{store.qty} available</span>
                          </span>
                          {store.location?.address?.address_line1 && (
                            <span className="block text-xs text-gray-500">
                              {store.location.address.address_line1}, {store.location.address.locality}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              {/* Quantity */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Quantity:</span>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 hover:bg-gray-100 transition-colors"
                    disabled={!onlineAvailable}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const max = maxQuantity ?? Infinity;
                      setQuantity(Math.min(max, Math.max(1, parseInt(e.target.value) || 1)));
                    }}
                    className="w-16 text-center border-x border-gray-300 py-2"
                    disabled={!onlineAvailable}
                  />
                  <button
                    onClick={() => setQuantity(Math.min(maxQuantity ?? Infinity, quantity + 1))}
                    className="px-4 py-2 hover:bg-gray-100 transition-colors"
                    disabled={!onlineAvailable || (item.quantities_by_location !== undefined && quantity >= maxQuantity)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Actions (outside the info box) */}
            {onlineAvailable ? (
              <div>
                <div className="flex gap-3">
                <button
                  onClick={handleAddToCart}
                  disabled={outOfStock}
                  className="flex-1 btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={outOfStock || buyingNow}
                  className="flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-medium bg-black text-white hover:bg-gray-800 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {buyingNow ? 'Processing…' : 'Buy Now'}
                </button>
                </div>
                {buyNowError && <p className="mt-3 text-sm text-red-600">{buyNowError}</p>}
              </div>
            ) : (
              <div className="flex items-center justify-center bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <span className="text-amber-700 font-semibold text-sm">Available In Store Only</span>
              </div>
            )}
          </motion.div>
        </div>

        {/* Item Details Table */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Item Details</h2>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50 w-1/3">Name</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{item.name}</td>
                </tr>
                {item.description && (
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">Set / Collection</td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-pre-line">{item.description}</td>
                  </tr>
                )}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">Price</td>
                  <td className="px-6 py-4 text-sm text-gray-900">${item.price.toFixed(2)}</td>
                </tr>
                {item.quantities_by_location !== undefined && (
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">Quantity Available</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{quantityAvailable}</td>
                  </tr>
                )}
                {item.categories?.length > 0 && (
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-500 bg-gray-50">Categories</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex flex-wrap gap-2">
                        {item.categories.map((cat) => (
                          <span key={cat} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogDetail;
